import React from 'react';
import { Container, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';

/**
 * IncidentTrustedAdult — surfaces the staff member a student chose to trust with this report
 * (FEAT-138 #138).
 *
 * A student may route their report to a specific trusted adult. The backend (assignResponder)
 * assigns that adult when they're available; if they were on leave at routing time it falls back
 * to normal (least-open) assignment and sets `trustedAdultUnavailable` so we can prompt a
 * responder to reassign / add the trusted adult back once they return. Reassign/add is done with
 * the existing "Assigned To" picker in the incident detail sidebar.
 *
 * Renders nothing when the student didn't choose a trusted adult.
 */
const IncidentTrustedAdult = ({ data }) => {
  const theme = useTheme();
  const trustedAdultId = data && data.trustedAdult;
  if (!trustedAdultId) return null;

  const name = data.trustedAdultName || 'a staff member';
  const unavailable = data.trustedAdultUnavailable === 'True';
  const routed = data.trustedAdultRouted === 'True';
  const warn = theme.palette.warning?.main || '#b26a00';
  const gray = theme.palette.custom?.gray || '#ccc';

  const style = {
    container: {
      display: 'flex', flexDirection: 'column',
      marginLeft: '60px', marginRight: '60px', marginTop: '40px', width: 'auto',
      paddingLeft: '0px !important',
      border: `1px solid ${unavailable ? warn : gray}`, borderRadius: '4px',
    },
    header: { borderBottom: `1px solid ${unavailable ? warn : gray}` },
    title: { fontSize: '20px', fontWeight: 700, marginTop: '20px', marginBottom: '20px', marginLeft: '20px' },
    body: { padding: '20px' },
    name: { fontSize: '16px', fontWeight: 600, marginBottom: '8px' },
    meta: { fontSize: '13px', color: theme.palette.text?.secondary || '#666', lineHeight: 1.5 },
    alert: { fontSize: '14px', fontWeight: 600, color: warn, lineHeight: 1.5 },
  };

  return (
    <Container sx={style.container}>
      <Container sx={style.header}>
        <Typography sx={style.title}>Requested trusted adult</Typography>
      </Container>
      <Box sx={style.body}>
        <Typography sx={style.name}>{name}</Typography>
        {unavailable ? (
          <Typography sx={style.alert}>
            {name} was unavailable (on leave) when this report came in, so it was routed to an
            available responder. If you want the trusted adult involved, reassign or add them using
            "Assigned To" in the sidebar once they're back.
          </Typography>
        ) : (
          <Typography sx={style.meta}>
            The student chose to send this report to {name}
            {routed ? ', and it was routed to them.' : '.'} You can still reassign or add responders
            using "Assigned To" in the sidebar.
          </Typography>
        )}
      </Box>
    </Container>
  );
};

export default IncidentTrustedAdult;
